import React, { useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react";
import {
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  CreditCardIcon,
  ClockIcon,
  CurrencyEuroIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Label from "components/Label/Label";

interface SubscriptionDetailPageProps {
  open: boolean;
  handleClose?: () => void;
  data: {
    id?: string;
    customer?: string;
    status?: string;
    created?: number;
    plan_name?: string;
    plan_amount?: string;
    plan_delai?: string;
    current_period_start?: number;
    current_period_end?: number;
    cancel_at?: number | null;
    cancel_at_period_end?: boolean;
    canceled_at?: number | null;
    payment_method_details?: {
      brand?: string;
      last4?: string;
      exp_month?: number;
      exp_year?: number;
    };
    [key: string]: any;
  } | null;
}

const SubscriptionDetailPage: React.FC<SubscriptionDetailPageProps> = ({ open, handleClose, data }) => {
  const [activeTab, setActiveTab] = useState("basic");

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const frenchLabels: { [key: string]: string } = {
    id: "ID",
    cancel_at: "Annulation prévue",
    cancel_at_period_end: "Annulation à la fin de la période",
    canceled_at: "Annulé le",
    created: "Créé le",
    current_period_start: "Début de la période",
    current_period_end: "Fin de la période",
    customer: "Client",
    payment_method_details: "Détails du paiement",
    status: "Statut",
    plan_name: "Abonnement",
    plan_amount_delai: "Montant/Délai",
    payment_method_brand: "Marque",
    payment_method_last4: "Derniers 4 chiffres",
    payment_method_expiry: "Expiration",
    cancellation_comment: "Commentaire",
    cancellation_feedback: "Feedback",
    cancellation_reason: "Raison",
  };

  const fieldIcons = {
    customer: UserIcon,
    status: TagIcon,
    created: CalendarIcon,
    plan_name: TagIcon,
    plan_amount_delai: CurrencyEuroIcon,
    payment_method_details: CreditCardIcon,
    current_period: ClockIcon,
    cancel_info: XMarkIcon,
  };

  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Détails complets", value: "details" },
  ];

  const renderValue = (value: any): React.ReactNode => {
    if (typeof value === "object" && value !== null) {
      return (
        <div className="ml-4 space-y-1">
          {Object.entries(value).map(([subKey, subValue]) => (
            <Typography key={subKey} className="text-sm font-medium text-gray-900 dark:text-white">
              {frenchLabels[subKey] || subKey} : {renderValue(subValue)}
            </Typography>
          ))}
        </div>
      );
    }

    if (typeof value === "boolean") {
      return value ? "Oui" : "Non";
    }

    if (typeof value === "number" && value.toString().length === 10) {
      return formatDate(value);
    }

    return String(value ?? "N/A");
  };

  const basicInfo = [
    {
      label: "Client",
      value: data?.email,
      icon: fieldIcons.customer,
    },
    {
      label: "Abonnement",
      value: data?.plan_name,
      icon: fieldIcons.plan_name,
    },
    {
      label: "Montant/Délai",
      value: data?.plan_amount && data?.plan_delai ? `${data.plan_amount} €/${data.plan_delai}` : "N/A",
      icon: fieldIcons.plan_amount_delai,
    },
    {
      label: "Créé le",
      value: formatDate(data?.created),
      icon: fieldIcons.created,
    },
  ];

  const detailedInfo = [
    {
      label: "Période actuelle",
      value: data?.current_period_start ? `${formatDate(data.current_period_start)}` : "N/A",
      icon: fieldIcons.current_period,
    },
    {
      label: "Fin période ",
      value: data?.current_period_end ? `${formatDate(data.current_period_end)}` : "N/A",
      icon: fieldIcons.current_period,
    },
    {
      label: "Détails du paiement",
      value: data?.payment_method_details
        ? {
            payment_method_brand: data.payment_method_details.brand,
            payment_method_last4: data.payment_method_details.last4,
            payment_method_expiry: `${data.payment_method_details.exp_month}/${data.payment_method_details.exp_year}`,
          }
        : "N/A",
      icon: fieldIcons.payment_method_details,
    },
    {
      label: "Informations sur l'annulation",
      value:
        data?.cancel_at || data?.canceled_at || data?.cancel_at_period_end
          ? {
              cancel_at: data.cancel_at ? formatDate(data.cancel_at) : null,
              canceled_at: data.canceled_at ? formatDate(data.canceled_at) : null,
              cancel_at_period_end: data.cancel_at_period_end,
              cancellation_reason: data.cancellation_reason,
              cancellation_feedback: data.cancellation_feedback,
              cancellation_comment: data.cancellation_comment,
            }
          : "Aucune",
      icon: fieldIcons.cancel_info,
    },
  ];

  return (
    <Dialog
      open={open}
      handler={() => console.log()}
      className="w-full max-w-3xl mx-auto transition-all duration-300 bg-white shadow-xl dark:bg-gray-900 rounded-2xl"
    >
      <DialogHeader className="flex items-center justify-between p-6 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-t-2xl">
        <Typography variant="h5" className="font-bold text-white">
          Détails de l'Abonnement
        </Typography>
        <button onClick={handleClose} className="text-white transition-colors hover:text-gray-200">
          <XMarkIcon className="w-6 h-6" />
        </button>
      </DialogHeader>

      <DialogBody className="p-6 max-h-[60vh] overflow-y-auto">
        <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
          <TabsHeader className="p-1 mb-4 bg-gray-100 rounded-lg dark:bg-gray-800">
            {tabs.map(({ label, value }) => (
              <Tab
                key={value}
                value={value}
                className="p-3 text-sm font-semibold text-gray-700 transition-colors rounded-md dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                {label}
              </Tab>
            ))}
          </TabsHeader>

          <TabsBody className="mt-2">
            <TabPanel value="basic" className="space-y-3">
              {basicInfo.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 transition-shadow rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="text-sm font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>

            <TabPanel value="details" className="space-y-3">
              {detailedInfo.map((item, index) => (
                <div
                  key={index}
                  className="p-4 transition-shadow rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                    {renderValue(item.value)}
                  </Typography>
                </div>
              ))}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </DialogBody>

      <DialogFooter className="flex justify-center p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
        <ButtonSecondary
          onClick={handleClose}
          className="px-6 py-2 text-gray-900 transition-colors bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Fermer
        </ButtonSecondary>
      </DialogFooter>
    </Dialog>
  );
};

export default SubscriptionDetailPage;
